import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { colors } from '../../constants/colors'
import HeaderProfile from './HeaderProfile'
import { useDashboardSocket } from '../../context/DashboardSocketContext'
import { joinChatSession } from '../../services/(adminapi)/messagesAPI'

const headerStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: '32px'
}

const headerLeftStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px'
}

const headerRightStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: '16px'
}

const titleStyle = {
  fontSize: '32px',
  fontWeight: '700',
  color: colors.text.primary,
  margin: 0
}

const headerIconStyle = {
  width: '24px',
  height: '24px',
  cursor: 'pointer',
  color: colors.text.secondary
}

const notificationWrapStyle = {
  position: 'relative'
}

const notificationDotStyle = {
  position: 'absolute',
  top: '-2px',
  right: '-2px',
  width: '8px',
  height: '8px',
  backgroundColor: '#ef4444',
  borderRadius: '50%',
  pointerEvents: 'none'
}

const dropdownPanelStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  width: 'min(360px, calc(100vw - 32px))',
  maxHeight: '380px',
  overflowY: 'auto',
  backgroundColor: colors.background?.primary || '#fff',
  border: `1px solid ${colors.borderVariants?.light || '#e2e8f0'}`,
  borderRadius: '10px',
  boxShadow: '0 10px 40px rgba(15, 23, 42, 0.12)',
  zIndex: 1000
}

const dropdownHeaderStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '12px 14px',
  borderBottom: `1px solid ${colors.borderVariants?.light || '#e2e8f0'}`
}

const dropdownTitleStyle = {
  margin: 0,
  fontSize: '14px',
  fontWeight: 600,
  color: colors.text.primary
}

const markReadBtnStyle = {
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: '12px',
  color: colors.text.secondary,
  fontWeight: 500
}

const emptyNoteStyle = {
  padding: '20px 14px',
  fontSize: '13px',
  color: colors.text.secondary,
  textAlign: 'center'
}

const notifRowOuterStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '8px',
  borderBottom: `1px solid ${colors.borderVariants?.light || '#f1f5f9'}`,
  padding: '4px 8px 4px 0'
}

const notifRowStyle = (read) => ({
  flex: 1,
  padding: '12px 8px 12px 14px',
  cursor: 'pointer',
  backgroundColor: read ? 'transparent' : 'rgba(59, 130, 246, 0.06)',
  textAlign: 'left',
  border: 'none',
  display: 'block',
  minWidth: 0
})

const notifTitleStyle = {
  fontSize: '13px',
  fontWeight: 600,
  color: colors.text.primary,
  marginBottom: '4px'
}

const notifBodyStyle = {
  fontSize: '12px',
  color: colors.text.secondary,
  lineHeight: 1.45
}

const notifMetaStyle = {
  fontSize: '11px',
  color: colors.text.secondary,
  marginTop: '6px',
  opacity: 0.85
}

const dismissBtnStyle = {
  flexShrink: 0,
  marginTop: '10px',
  marginRight: '6px',
  border: 'none',
  background: 'rgba(148, 163, 184, 0.2)',
  borderRadius: '4px',
  width: '22px',
  height: '22px',
  cursor: 'pointer',
  fontSize: '14px',
  lineHeight: 1,
  color: colors.text.secondary
}

const toastPopupStyle = {
  position: 'absolute',
  top: 'calc(100% + 8px)',
  right: 0,
  width: 'min(420px, calc(100vw - 32px))',
  backgroundColor: colors.background?.primary || '#fff',
  border: `2px solid ${colors.primary || '#3b82f6'}`,
  borderRadius: '12px',
  boxShadow: '0 16px 48px rgba(15, 23, 42, 0.22)',
  zIndex: 1001,
  padding: '18px 18px 16px'
}

const toastHeaderStyle = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '8px',
  marginBottom: '6px'
}

const toastCloseStyle = {
  flexShrink: 0,
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: '18px',
  lineHeight: 1,
  color: colors.text.secondary,
  padding: 0
}

const toastActionStyle = {
  marginTop: '12px',
  padding: '8px 14px',
  borderRadius: '6px',
  border: 'none',
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  color: '#fff',
  backgroundColor: colors.primary || '#3b82f6'
}

const toastSecondaryActionStyle = {
  marginTop: '12px',
  marginLeft: '8px',
  padding: '8px 14px',
  borderRadius: '6px',
  border: `1px solid ${colors.borderVariants?.light || '#e2e8f0'}`,
  cursor: 'pointer',
  fontSize: '13px',
  fontWeight: 600,
  color: colors.text.secondary,
  backgroundColor: 'transparent'
}

const toastActionsRowStyle = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center'
}

// Clears on full page refresh; survives SPA route changes so the same alert is not re-toasted.
const toastedNotificationIds = new Set()

/**
 * Shared top bar for tab pages: title on left; help, notification, and profile on right.
 * Tab-specific action buttons (Add Intent, Upload File, etc.) should be rendered below this component.
 */
export default function PageTopBar({ title, onAiConfig, onEmbeddingGuide, onSignOut }) {
  const navigate = useNavigate()
  const { user } = useSelector((s) => s.auth)
  const {
    sessionNotifications,
    unreadCount,
    markNotificationRead,
    markAllNotificationsRead,
    dismissNotification
  } = useDashboardSocket()

  const [panelOpen, setPanelOpen] = useState(false)
  const [toastNotif, setToastNotif] = useState(null)
  const [joiningFromToast, setJoiningFromToast] = useState(false)
  const wrapRef = useRef(null)
  const isInitialNotifsRef = useRef(true)
  const knownNotifIdsRef = useRef(new Set())

  const handleAiConfig = onAiConfig ?? (() => navigate('/ai-configuration'))
  const handleEmbeddingGuide = onEmbeddingGuide ?? (() => navigate('/embedding-guide'))

  useEffect(() => {
    if (!panelOpen && !toastNotif) return undefined
    const onDocMouseDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setPanelOpen(false)
        setToastNotif(null)
      }
    }
    document.addEventListener('mousedown', onDocMouseDown)
    return () => document.removeEventListener('mousedown', onDocMouseDown)
  }, [panelOpen, toastNotif])

  const showToastForNotification = (n) => {
    if (!n || toastedNotificationIds.has(n.id)) return
    toastedNotificationIds.add(n.id)
    setToastNotif(n)
  }

  useEffect(() => {
    const ids = new Set(sessionNotifications.map((n) => n.id))
    if (isInitialNotifsRef.current) {
      isInitialNotifsRef.current = false
      knownNotifIdsRef.current = ids
      const latestUnread = sessionNotifications.find((n) => !n.read)
      showToastForNotification(latestUnread)
      return
    }
    const added = sessionNotifications.find((n) => !knownNotifIdsRef.current.has(n.id))
    knownNotifIdsRef.current = ids
    showToastForNotification(added)
  }, [sessionNotifications])

  useEffect(() => {
    if (!toastNotif) return undefined
    const t = setTimeout(() => setToastNotif(null), 12000)
    return () => clearTimeout(t)
  }, [toastNotif])

  const handleJoinFromNotification = async (n) => {
    if (!n?.chatSessionId || joiningFromToast) return
    setJoiningFromToast(true)
    try {
      await joinChatSession(n.chatSessionId)
      markNotificationRead(n.id)
      dismissNotification(n.id)
      setToastNotif(null)
      if (n.conversationId) {
        navigate(`/chat-logs?conversation=${encodeURIComponent(n.conversationId)}`)
      } else {
        navigate('/chat-logs')
      }
    } catch {
      onNotificationClick(n)
      setToastNotif(null)
    } finally {
      setJoiningFromToast(false)
    }
  }

  const togglePanel = () => {
    setPanelOpen((o) => !o)
  }

  const onNotificationClick = (n) => {
    markNotificationRead(n.id)
    setPanelOpen(false)
    if (n.conversationId) {
      navigate(`/chat-logs?conversation=${encodeURIComponent(n.conversationId)}`)
    } else {
      navigate('/chat-logs')
    }
  }

  return (
    <div style={headerStyle}>
      <div style={headerLeftStyle}>
        <h1 style={titleStyle}>{title}</h1>
      </div>
      <div style={headerRightStyle}>
        <svg style={headerIconStyle} fill="currentColor" viewBox="0 0 24 24" aria-label="Help">
          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
        </svg>
        <div ref={wrapRef} style={notificationWrapStyle}>
          <button
            type="button"
            onClick={togglePanel}
            aria-label="Notifications"
            aria-expanded={panelOpen}
            style={{
              ...headerIconStyle,
              position: 'relative',
              background: 'none',
              border: 'none',
              padding: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <svg style={{ width: '24px', height: '24px' }} fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 22c1.1 0 2-.9 2-2h-4c0 1.1.89 2 2 2zm6-6v-5c0-3.07-1.64-5.64-4.5-6.32V4c0-.83-.67-1.5-1.5-1.5s-1.5.67-1.5 1.5v.68C7.63 5.36 6 7.92 6 11v5l-2 2v1h16v-1l-2-2z"/>
            </svg>
            {unreadCount > 0 ? <span style={notificationDotStyle} aria-hidden /> : null}
          </button>
          {toastNotif && !panelOpen ? (
            <div
              style={toastPopupStyle}
              role="alert"
              aria-live="polite"
              onClick={(e) => e.stopPropagation()}
            >
              <div style={toastHeaderStyle}>
                <div style={notifTitleStyle}>{toastNotif.title}</div>
                <button
                  type="button"
                  style={toastCloseStyle}
                  aria-label="Dismiss popup"
                  onClick={() => {
                    if (toastNotif?.id) toastedNotificationIds.add(toastNotif.id)
                    setToastNotif(null)
                  }}
                >
                  ×
                </button>
              </div>
              <div style={notifBodyStyle}>{toastNotif.body}</div>
              <div style={toastActionsRowStyle}>
                {user?.role === 'Agent' && toastNotif.kind === 'queued' && toastNotif.chatSessionId ? (
                  <button
                    type="button"
                    style={toastActionStyle}
                    disabled={joiningFromToast}
                    onClick={() => handleJoinFromNotification(toastNotif)}
                  >
                    {joiningFromToast ? 'Joining…' : 'Join chat'}
                  </button>
                ) : null}
                <button
                  type="button"
                  style={
                    user?.role === 'Agent' && toastNotif.kind === 'queued'
                      ? toastSecondaryActionStyle
                      : toastActionStyle
                  }
                  onClick={() => {
                    onNotificationClick(toastNotif)
                    setToastNotif(null)
                  }}
                >
                  Open Chat Logs
                </button>
              </div>
            </div>
          ) : null}
          {panelOpen ? (
            <div style={dropdownPanelStyle} role="region" aria-label="Notification list">
              <div style={dropdownHeaderStyle}>
                <h2 style={dropdownTitleStyle}>Notifications</h2>
                {sessionNotifications.length > 0 && unreadCount > 0 ? (
                  <button
                    type="button"
                    style={markReadBtnStyle}
                    onClick={() => markAllNotificationsRead()}
                  >
                    Mark all read
                  </button>
                ) : null}
              </div>
              {sessionNotifications.length === 0 ? (
                <div style={emptyNoteStyle}>No notifications yet.</div>
              ) : (
                sessionNotifications.map((n) => (
                  <div key={n.id} style={notifRowOuterStyle}>
                    <button
                      type="button"
                      style={notifRowStyle(n.read)}
                      onClick={() => onNotificationClick(n)}
                    >
                      <div style={notifTitleStyle}>{n.title}</div>
                      <div style={notifBodyStyle}>{n.body}</div>
                      <div style={notifMetaStyle}>
                        {new Date(n.createdAt).toLocaleString()}
                      </div>
                    </button>
                    <button
                      type="button"
                      style={dismissBtnStyle}
                      aria-label="Dismiss"
                      onClick={(e) => {
                        e.stopPropagation()
                        dismissNotification(n.id)
                      }}
                    >
                      ×
                    </button>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </div>
        <HeaderProfile
          onAiConfig={handleAiConfig}
          onEmbeddingGuide={handleEmbeddingGuide}
          onSignOut={onSignOut}
        />
      </div>
    </div>
  )
}
